import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Checkbox,
  SimpleGrid,
  Text,
  Image,
  IconButton,
  Wrap,
  WrapItem,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Tag,
  TagLabel,
} from "@chakra-ui/react";
import { MdClose, MdVideoLibrary } from "react-icons/md";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT } from "constants/brandColors";
import { validateNewsForm, MIN_TEXT_WORDS, stripHtml } from "./newsValidation";
import { getTranslatedField, useLocales, REQUIRED_LOCALE_CODE } from "utils/translations";
import RichTextEditor from "components/richTextEditor/RichTextEditor";

const NEWS_PAGE_ID = PAGE_IDS.NEWS;
const TAGS_PAGE_ID = PAGE_IDS.TAGS;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

const emptyTranslation = () => ({ title: "", header: "", text: "", slug: "", metaTitle: "", metaDesc: "" });

function CreateNews({ onCreated }) {
  const { t } = useTranslation();
  const { locales } = useLocales();
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [priority, setPriority] = useState(false);
  const [breakingNews, setBreakingNews] = useState(false);
  const [status, setStatus] = useState("DRAFT");
  const [publisherBy, setPublisherBy] = useState("");
  const [translations, setTranslations] = useState({ en: emptyTranslation(), ar: emptyTranslation() });
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [videos, setVideos] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [categoriesRes, subcategoriesRes, tagsRes] = await Promise.all([
          fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/category`),
          fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/sub/category`),
          fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/tag?page_id=${TAGS_PAGE_ID}`),
        ]);
        setCategories(Array.isArray(categoriesRes) ? categoriesRes : []);
        setSubcategories(Array.isArray(subcategoriesRes) ? subcategoriesRes : []);
        setTagOptions(tagsRes?.data || []);
      } catch (err) {
        console.error("Error fetching category/subcategory/tag options:", err);
      }
    };
    fetchOptions();
  }, []);

  // Locales load asynchronously from /api/language — as new ones arrive
  // (e.g. French added after mount), grow the translations map to include
  // them without wiping anything the author already typed for en/ar.
  useEffect(() => {
    setTranslations((prev) => {
      const next = { ...prev };
      let changed = false;
      locales.forEach(({ code }) => {
        if (!next[code]) {
          next[code] = emptyTranslation();
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [locales]);

  // Build/revoke object URLs so the user can actually see which images they
  // picked before uploading, instead of just a file count.
  useEffect(() => {
    const urls = images.map((file) => URL.createObjectURL(file));
    setImagePreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [images]);

  const subcategoryOptions = subcategories.filter(
    (s) => !categoryId || s.categoryId === Number(categoryId)
  );

  // `prev[locale]` can briefly be missing for a locale that just became
  // active (locales loads from the API a render before the effect above
  // adds a matching key to `translations`) — fall back to a full empty
  // shape instead of spreading undefined, so no field silently drops out.
  const setTranslationField = (locale, field, value) => {
    setTranslations((prev) => ({ ...prev, [locale]: { ...emptyTranslation(), ...prev[locale], [field]: value } }));
  };

  const toggleTag = (tagId) => {
    setSelectedTagIds((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]));
  };

  // Each time the file picker is opened, e.target.files only holds that
  // dialog's selection — it doesn't remember earlier picks. So we append to
  // the existing list instead of replacing it, letting the user add images
  // one at a time across multiple picker opens without losing prior ones.
  const handleImagesChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setImages((prev) => [...prev, ...newFiles].slice(0, 10));
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleVideosChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setVideos((prev) => [...prev, ...newFiles].slice(0, 5));
    e.target.value = "";
  };

  const removeVideo = (index) => {
    setVideos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const errors = validateNewsForm({ categoryId, translations, status, publisherBy, locales }, t);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const translationsPayload = [];
      locales.forEach(({ code }) => {
        const tr = translations[code] || emptyTranslation();
        if (!tr.title.trim() && !stripHtml(tr.text).trim()) return;
        translationsPayload.push({
          locale: code,
          title: tr.title.trim(),
          ...(tr.header.trim() && { header: tr.header.trim() }),
          text: tr.text,
          ...(tr.slug.trim() && { slug: tr.slug.trim() }),
          ...(tr.metaTitle.trim() && { metaTitle: tr.metaTitle.trim() }),
          ...(tr.metaDesc.trim() && { metaDesc: tr.metaDesc.trim() }),
        });
      });

      const formData = new FormData();
      formData.append("page_id", NEWS_PAGE_ID);
      formData.append("category_id", categoryId);
      if (subcategoryId) formData.append("subcategory_id", subcategoryId);
      formData.append("priority", priority ? "true" : "false");
      formData.append("breaking_news", breakingNews ? "true" : "false");
      formData.append("status", status);
      if (status === "PUBLISHED") formData.append("publisher_by", publisherBy.trim());
      formData.append("translations", JSON.stringify(translationsPayload));
      if (selectedTagIds.length > 0) formData.append("tags", JSON.stringify(selectedTagIds));
      images.forEach((file) => formData.append("Images", file));
      videos.forEach((file) => formData.append("Videos", file));

      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/news`,
        "POST",
        formData
      );
      if (!response) {
        throw new Error("Failed to create news");
      }
      onCreated?.();
    } catch (err) {
      setError(err.message || t("Failed to create news. Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTranslationTab = (code) => {
    const tr = translations[code] || emptyTranslation();
    const localeErrors = fieldErrors[code] || {};
    const wordCount = stripHtml(tr.text).trim().split(/\s+/).filter(Boolean).length;
    const isRtl = code === "ar";

    return (
      <Box>
        <FormControl isInvalid={!!localeErrors.title} mt="8px">
          <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Title")}</FormLabel>
          <Input
            dir={isRtl ? "rtl" : "ltr"}
            placeholder={t("The news headline, as it will appear in the list")}
            value={tr.title}
            onChange={(e) => setTranslationField(code, "title", e.target.value)}
            {...fieldProps}
          />
          <FormErrorMessage>{localeErrors.title}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!localeErrors.header} mt="16px">
          <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Header")}</FormLabel>
          <Input
            dir={isRtl ? "rtl" : "ltr"}
            placeholder={t("A short subtitle or lede shown under the title")}
            value={tr.header}
            onChange={(e) => setTranslationField(code, "header", e.target.value)}
            {...fieldProps}
          />
          <FormErrorMessage>{localeErrors.header}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!localeErrors.text} mt="16px">
          <Flex justify="space-between" align="center">
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT} mb="0">{t("Text")}</FormLabel>
            <Text fontSize="xs" color={wordCount < MIN_TEXT_WORDS ? "red.500" : "gray.500"}>
              {t("{{count}} words (minimum {{min}})", { count: wordCount, min: MIN_TEXT_WORDS })}
            </Text>
          </Flex>
          <RichTextEditor
            dir={isRtl ? "rtl" : "ltr"}
            placeholder={t("Write the full article body here — at least {{min}} words, not just a headline restated.", { min: MIN_TEXT_WORDS })}
            value={tr.text}
            onChange={(html) => setTranslationField(code, "text", html)}
            isInvalid={!!localeErrors.text}
          />
          <FormErrorMessage>{localeErrors.text}</FormErrorMessage>
        </FormControl>

        <FormControl mt="16px">
          <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Slug")}</FormLabel>
          <Input placeholder={t("Leave blank to auto-generate")} value={tr.slug} onChange={(e) => setTranslationField(code, "slug", e.target.value)} {...fieldProps} />
        </FormControl>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt="16px">
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Meta Title (SEO)")}</FormLabel>
            <Input value={tr.metaTitle} onChange={(e) => setTranslationField(code, "metaTitle", e.target.value)} {...fieldProps} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Meta Description (SEO)")}</FormLabel>
            <Input value={tr.metaDesc} onChange={(e) => setTranslationField(code, "metaDesc", e.target.value)} {...fieldProps} />
          </FormControl>
        </SimpleGrid>
      </Box>
    );
  };

  return (
    <FormControl>
      <form onSubmit={handleSubmit}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl isInvalid={!!fieldErrors.categoryId}>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Category")}</FormLabel>
            <Select
              isRequired
              placeholder={t("Select a category")}
              value={categoryId}
              onChange={(e) => { setCategoryId(e.target.value); setSubcategoryId(""); }}
              {...fieldProps}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{getTranslatedField(c.translations, "en")}</option>
              ))}
            </Select>
            <FormErrorMessage>{fieldErrors.categoryId}</FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Sub Category")}</FormLabel>
            <Select
              placeholder={t("Select a sub category (optional)")}
              value={subcategoryId}
              onChange={(e) => setSubcategoryId(e.target.value)}
              {...fieldProps}
            >
              {subcategoryOptions.map((s) => (
                <option key={s.id} value={s.id}>{getTranslatedField(s.translations, "en")}</option>
              ))}
            </Select>
          </FormControl>
        </SimpleGrid>

        <Tabs mt="16px" colorScheme="gray" variant="enclosed">
          <TabList overflowX="auto" overflowY="hidden" flexWrap="nowrap" sx={{ scrollbarWidth: "thin" }}>
            {locales.map(({ code, label }) => (
              <Tab key={code} whiteSpace="nowrap" flexShrink={0} _selected={{ color: GOLD_TEXT, borderColor: "gray.200", borderBottomColor: "white", fontWeight: "700" }}>
                {label}
                {code === REQUIRED_LOCALE_CODE && <Text as="span" color="red.500" ml="4px">*</Text>}
              </Tab>
            ))}
          </TabList>
          <TabPanels borderWidth="1px" borderTop="none" borderColor="gray.200" borderBottomRadius="md" p="12px">
            {locales.map(({ code }) => (
              <TabPanel key={code} p="4px">
                {renderTranslationTab(code)}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>

        <Box mt="16px">
          <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Tags")}</FormLabel>
          {tagOptions.length === 0 ? (
            <Text fontSize="sm" color="gray.500">{t("No tags yet — create one from the Tags page.")}</Text>
          ) : (
            <Wrap>
              {tagOptions.map((tagOption) => {
                const isSelected = selectedTagIds.includes(tagOption.id);
                return (
                  <WrapItem key={tagOption.id}>
                    <Tag
                      as="button"
                      type="button"
                      onClick={() => toggleTag(tagOption.id)}
                      bg={isSelected ? GOLD_BORDER : "gray.100"}
                      color={isSelected ? "#1A1A1A" : "gray.700"}
                      cursor="pointer"
                      borderRadius="full"
                    >
                      <TagLabel>{getTranslatedField(tagOption.translations, "en")}</TagLabel>
                    </Tag>
                  </WrapItem>
                );
              })}
            </Wrap>
          )}
        </Box>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt="16px">
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Status")}</FormLabel>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} {...fieldProps}>
              <option value="DRAFT">{t("Draft")}</option>
              <option value="PUBLISHED">{t("Published")}</option>
              <option value="ARCHIVED">{t("Archived")}</option>
            </Select>
          </FormControl>
          <Checkbox mt="36px" isChecked={priority} onChange={(e) => setPriority(e.target.checked)} colorScheme="brand">
            {t("Priority")}
          </Checkbox>
          <Checkbox mt="36px" isChecked={breakingNews} onChange={(e) => setBreakingNews(e.target.checked)} colorScheme="brand">
            {t("Breaking News")}
          </Checkbox>
        </SimpleGrid>

        {status === "PUBLISHED" && (
          <FormControl isInvalid={!!fieldErrors.publisherBy} mt="16px">
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Published By")}</FormLabel>
            <Input
              isRequired
              placeholder={t("Name of the publisher")}
              value={publisherBy}
              onChange={(e) => setPublisherBy(e.target.value)}
              {...fieldProps}
            />
            <FormErrorMessage>{fieldErrors.publisherBy}</FormErrorMessage>
          </FormControl>
        )}

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt="16px">
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Images (up to 10)")}</FormLabel>
            <Input
              type="file"
              accept="image/*"
              multiple
              p="6px"
              {...fieldProps}
              onChange={handleImagesChange}
            />
            <Text fontSize="xs" color="gray.500" mt="4px">
              {t("Select several at once, or open this picker again to add more one at a time — earlier picks are kept.")}
            </Text>
            {imagePreviews.length > 0 && (
              <Wrap mt="8px" spacing={2}>
                {imagePreviews.map((src, index) => (
                  <WrapItem key={src} position="relative">
                    <Image src={src} alt={images[index]?.name} boxSize="64px" objectFit="cover" borderRadius="md" borderWidth="1px" borderColor="gray.200" />
                    <IconButton
                      aria-label={t("Remove image")}
                      icon={<MdClose />}
                      size="xs"
                      colorScheme="red"
                      position="absolute"
                      top="-6px"
                      right="-6px"
                      borderRadius="full"
                      onClick={() => removeImage(index)}
                    />
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Videos (up to 5)")}</FormLabel>
            <Input
              type="file"
              accept="video/*"
              multiple
              p="6px"
              {...fieldProps}
              onChange={handleVideosChange}
            />
            <Text fontSize="xs" color="gray.500" mt="4px">
              {t("Select several at once, or open this picker again to add more one at a time — earlier picks are kept.")}
            </Text>
            {videos.length > 0 && (
              <Wrap mt="8px" spacing={2}>
                {videos.map((file, index) => (
                  <WrapItem key={`${file.name}-${index}`}>
                    <Flex align="center" gap={1} borderWidth="1px" borderColor="gray.200" borderRadius="md" px={2} py={1}>
                      <MdVideoLibrary />
                      <Text fontSize="xs" noOfLines={1} maxW="120px">{file.name}</Text>
                      <IconButton
                        aria-label={t("Remove video")}
                        icon={<MdClose />}
                        size="xs"
                        variant="ghost"
                        onClick={() => removeVideo(index)}
                      />
                    </Flex>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </FormControl>
        </SimpleGrid>

        <Button
          type="submit"
          fontSize="sm"
          bg={GOLD_BORDER}
          color="#1A1A1A"
          _hover={{ bg: GOLD_TEXT, color: "white" }}
          _active={{ bg: GOLD_TEXT, color: "white" }}
          fontWeight="500"
          w="100%"
          h="50px"
          mt="20px"
          mb="8px"
          isLoading={isSubmitting}
        >
          {t("Create News")}
        </Button>
        {error && (
          <Box color="red.500" mt={2} whiteSpace="pre-line">
            {error}
          </Box>
        )}
      </form>
    </FormControl>
  );
}

export default CreateNews;
